// In-app replacements for window.confirm / window.prompt. Call confirmDialog() or promptDialog() from anywhere (they return a
// promise); <AskDialogHost /> (mounted once in App) renders the dialog. Keyboard: Enter confirms, Escape cancels.
import { useEffect, useRef, useState } from "react";
import { AlertDialog, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { t } from "@/i18n";

let push = null;
const queue = [];

function ask(opts) {
  return new Promise((resolve) => {
    const req = { ...opts, resolve };
    if (push) push(req);
    else queue.push(req);
  });
}

/** Resolves true or false. opts: { title, body, confirmLabel, cancelLabel, destructive, testid } */
export const confirmDialog = (opts) => ask({ kind: "confirm", ...opts });

/** Resolves an object of field values, or null when cancelled.
 *  opts: { title, body, confirmLabel, destructive, testid, fields: [{ name, label, defaultValue, placeholder, required, minLength, type, inputMode }] } */
export const promptDialog = (opts) => ask({ kind: "prompt", ...opts });

const btn = "inline-flex items-center justify-center gap-1.5 rounded-lg border border-black/10 px-3 py-2 text-sm font-medium hover:bg-slate-50 disabled:opacity-50";

export function AskDialogHost() {
  const [cur, setCur] = useState(null);
  const [vals, setVals] = useState({});
  const pending = useRef([]);
  useEffect(() => {
    push = (req) => setCur((c) => { if (c) { pending.current.push(req); return c; } return req; });
    while (queue.length) push(queue.shift());
    return () => { push = null; };
  }, []);
  useEffect(() => {
    if (cur?.kind === "prompt") setVals(Object.fromEntries((cur.fields || []).map((f) => [f.name, f.defaultValue ?? ""])));
  }, [cur]);
  if (!cur) return null;
  const fields = cur.kind === "prompt" ? cur.fields || [] : [];
  const valid = fields.every((f) => {
    const v = String(vals[f.name] ?? "").trim();
    return (!f.required || v) && (!f.minLength || !v || v.length >= f.minLength) && (!f.minLength || !f.required || v.length >= f.minLength);
  });
  const done = (ok) => {
    cur.resolve(cur.kind === "confirm" ? ok : ok ? vals : null);
    setCur(pending.current.shift() || null);
  };
  const tid = cur.testid || "ask-dialog";
  return (
    <AlertDialog open onOpenChange={(o) => { if (!o) done(false); }}>
      <AlertDialogContent data-testid={tid}>
        <form onSubmit={(e) => { e.preventDefault(); if (valid) done(true); }} className="grid gap-4">
          <AlertDialogHeader>
            <AlertDialogTitle>{cur.title}</AlertDialogTitle>
            {cur.body && <AlertDialogDescription className="whitespace-pre-line">{cur.body}</AlertDialogDescription>}
          </AlertDialogHeader>
          {fields.map((f, i) => (
            <label key={f.name} className="grid gap-1 text-sm">{f.label}
              <input autoFocus={i === 0} type={f.type || "text"} inputMode={f.inputMode} value={vals[f.name] ?? ""} placeholder={f.placeholder} maxLength={f.maxLength || 500}
                onChange={(e) => setVals((v) => ({ ...v, [f.name]: e.target.value }))} data-testid={`${tid}-${f.name}`}
                className="rounded-xl border border-black/10 bg-white px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#0EA5A0]/40" />
              {f.hint && <span className="text-xs text-slate-500">{f.hint}</span>}
            </label>))}
          <AlertDialogFooter>
            <button type="button" className={btn} onClick={() => done(false)} data-testid={`${tid}-cancel`}>{cur.cancelLabel || t("Cancel")}</button>
            <button type="submit" autoFocus={!fields.length} disabled={!valid} data-testid={`${tid}-confirm`}
              className={`${btn} ${cur.destructive ? "border-red-600 bg-red-600 text-white hover:bg-red-700" : "border-[#0B1A2E] bg-[#0B1A2E] text-white hover:bg-[#0B1A2E]/90"}`}>
              {cur.confirmLabel || t("Continue")}</button>
          </AlertDialogFooter>
        </form>
      </AlertDialogContent>
    </AlertDialog>
  );
}
